import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export function DeleteTableDialog({
  open,
  onOpenChange,
  schema,
  table,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  table: string;
  onSuccess: () => void;
}) {
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDelete = confirmation === table;

  async function handleDelete() {
    if (!canDelete) return;
    setError(null);
    setDeleting(true);

    try {
      const res = await fetch(
        `/api/database/manage-table?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`,
        { method: "DELETE" }
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to delete table");
        return;
      }
      onSuccess();
      onOpenChange(false);
      setConfirmation("");
      toast.success(`Table "${table}" deleted`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setConfirmation(""); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">Delete Table</DialogTitle>
          <DialogDescription>
            This will permanently delete the table{" "}
            <span className="font-mono text-foreground">{schema}.{table}</span>{" "}
            and all its data. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Type <span className="font-mono font-medium text-foreground">{table}</span> to confirm:
          </p>
          <Input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={table}
            className="h-8 text-sm font-mono"
            autoFocus
          />
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canDelete || deleting}
          >
            {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Table
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
